import React, { useEffect, useState } from "react";

import axios from "axios";
import { useNavigate, useOutletContext } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PaginatedSortableTable from "../components/PaginatedSortableTable";
import PaginatedSortableControls from "../components/PaginatedSortableControls";
dayjs.extend(utc);
dayjs.extend(timezone);

export default function NewGame() {
  const API_BASE = process.env.REACT_APP_API_BASE_URL;
  const { setIsLoggedIn } = useOutletContext();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [newGameList, setNewGameList] = useState([]);
  const [GameList, setGameList] = useState([]);
  const [VendorList, setVendorList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const rowsPerPageOptions = [10, 20, 50, 100];
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [korName, setKorName] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchVendorList = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(`${API_BASE}/api/MyVendorList`, {
        params: {
          userId: user.user_id,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setVendorList(res.data.List);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (
          err.response.status === 401 ||
          err.response.status === 403 ||
          err.response.status === 500
        ) {
          localStorage.removeItem("token");
          setIsLoggedIn(false);
          navigate("/login");
          return;
        }
      }
      console.error("유저 정보 불러오기 실패:", err);
    }
  };

  const [sortConfig, setSortConfig] = useState({
    field: "createdAt",
    order: "desc",
  });

  const normalize = (v) =>
    String(v ?? "")
      .trim()
      .toLowerCase();

  const gameMap = React.useMemo(() => {
    const map = new Map();

    GameList.forEach((g) => {
      const key = `${normalize(g.vcode)}__${normalize(g.game_id)}`;

      map.set(key, {
        game_name: g.game_name,
        game_name_kor: g.game_name_kor,
      });
    });

    return map;
  }, [GameList]);
  useEffect(() => {
    const storedUser = localStorage.getItem("first");

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (e) {
        console.error("유저 정보 파싱 오류:", e);
      }
    }
  }, []);
  const fetchLogsData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/api/NewGame`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data?.success) {
        setNewGameList(res.data.list);
        setCurrentPage(1); // 새 조회 시 페이지 리셋
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if ([401, 403, 500].includes(err.response.status)) {
          localStorage.removeItem("token");
          setIsLoggedIn(false);
          navigate("/login");
          return;
        }
      }
      console.error("조회 실패:", err);
    }
  };

  const fetchGameList = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/api/GameList`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // ✅ success 체크 제거
      if (Array.isArray(res.data?.vendor_list)) {
        setGameList(res.data.vendor_list);
      } else {
        console.error("GameList 응답 이상:", res.data);
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if ([401, 403, 500].includes(err.response.status)) {
          localStorage.removeItem("token");
          setIsLoggedIn(false);
          navigate("/login");
          return;
        }
      }
      console.error("GameList 조회 실패:", err);
    }
  };

  useEffect(() => {
    fetchLogsData();
    fetchGameList();
    fetchVendorList();
  }, [user]);

  const sortedData = [...newGameList].sort((a, b) => {
    const aVal = a[sortConfig.field];
    const bVal = b[sortConfig.field];

    if (sortConfig.order === "asc") return aVal > bVal ? 1 : -1;
    return aVal < bVal ? 1 : -1;
  });

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const pagedData = sortedData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const vendorMap = React.useMemo(() => {
    const map = new Map();

    VendorList.forEach((v) => {
      if (v.v_code) {
        map.set(v.v_code.trim(), v.v_name_kor?.trim() || v.v_code);
      }
    });

    return map;
  }, [VendorList]);

  const renderRow = (row) => {
    const key = `${normalize(row.vendorKey)}__${normalize(row.gameId)}`;
    const gameInfo = gameMap.get(key);

    // ⭐ 여기 핵심
    const vendorKorName =
      vendorMap.get(normalize(row.vendorKey)) || row.vendorKey;

    return (
      <tr key={row.idx}>
        <td className="text-center">{row.gameId}</td>

        <td className="text-center">
          <span className="badge bg-outline-info">{vendorKorName}</span>
        </td>

        <td className="text-center">
          {gameInfo?.game_name_kor ? (
            gameInfo.game_name_kor
          ) : (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => {
                setSelectedRow({
                  gameId: row.gameId,
                  vendorKey: row.vendorKey,
                });
                setKorName("");
                setShowModal(true);
              }}
            >
              등록하기
            </button>
          )}
        </td>

        <td className="text-center">{gameInfo?.game_name ?? "-"}</td>

        <td className="text-center">
          {dayjs(row.createdAt).tz("Asia/Seoul").format("YYYY-MM-DD HH:mm:ss")}
        </td>
      </tr>
    );
  };

  return (
    <div className="nk-content">
      <div className="container-fluid">
        <div className="nk-content-inner">
          <div className="nk-content-body">
            <div className="nk-block d-flex vLogsAgent">
              <div className="w-100 card card-bordered">
                <div className="card-inner-group">
                  <div className="card-inner tableBox px-0 pb-0">
                    <div className="table-wrapper">
                      <div className="table-responsive">
                        <div data-pattern="priority-columns">
                          <PaginatedSortableControls
                            currentPage={currentPage}
                            totalPages={totalPages}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={rowsPerPageOptions}
                            sortConfig={sortConfig}
                            sortOptions={[
                              { value: "createdAt:desc", label: "등록일 ↓" },
                              { value: "createdAt:asc", label: "등록일 ↑" },
                              { value: "gameId:asc", label: "게임ID ↑" },
                              { value: "gameId:desc", label: "게임ID ↓" },
                            ]}
                            onPageChange={setCurrentPage}
                            onRowsPerPageChange={(v) => {
                              setRowsPerPage(v);
                              setCurrentPage(1);
                            }}
                            onSortChange={(field, order) =>
                              setSortConfig({ field, order })
                            }
                          />
                          <table
                            cellSpacing="0"
                            className="table table-small-font table-tighten table-bordered table-striped"
                          >
                            <thead>
                              <tr data-pos-y="0">
                                <th
                                  className="text-center"
                                  data-pos-x="3"
                                  id="gameList-col-3"
                                >
                                  게임아이디
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="4"
                                  id="gameList-col-4"
                                >
                                  밴더사
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="5"
                                  id="gameList-col-5"
                                >
                                  게임명(KR)
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="6"
                                  id="gameList-col-6"
                                >
                                  게임명(EN)
                                </th>
                                <th
                                  className="text-center"
                                  data-pos-x="7"
                                  id="gameList-col-7"
                                >
                                  등록일
                                </th>
                              </tr>
                            </thead>

                            <PaginatedSortableTable
                              data={pagedData}
                              renderRow={renderRow}
                            />
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* <div className="card-inner">
                    <div className="row justify-end">
                      <div className="col-12 col-md-4 col-xxl-4">
                        <ul
                          className="pagination justify-content-center justify-content-md-start"
                          style={{
                            float: "right",
                          }}
                        >
                          <li className="page-item">
                            <a className="page-link disabled" href="#">
                              <i className="fas fa-angle-double-left" />
                              <font>처음</font>
                            </a>
                          </li>
                          <li className="page-item">
                            <a className="page-link disabled" href="#">
                              <i className="fa fa-angle-left" />
                              <font>이전</font>
                            </a>
                          </li>
                          <li className="page-item">
                            <input
                              className="form-control text-center"
                              type="number"
                            />
                          </li>
                          <li className="page-item">
                            <a className="page-link" href="#">
                              <font>다음</font>
                              <i className="fa fa-angle-right" />
                            </a>
                          </li>
                        </ul>
                      </div>
                      <div className="col-6 col-md-2 col-xxl-2">
                        <select className="form-select">
                          <option value="10">리스트 - 10</option>
                          <option value="20">리스트 - 20</option>
                          <option value="50">리스트 - 50</option>
                          <option value="100">리스트 - 100</option>
                        </select>
                      </div>
                      <div className="col-6 col-md-2 col-xxl-2">
                        <select className="form-select">
                          <option value="-1">처리시간 - 내림차순</option>
                          <option value="1">처리시간 - 오름차순</option>
                        </select>
                      </div>
                    </div>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">게임명(KR) 등록</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                <div className="mb-2">
                  <strong>게임 ID :</strong> {selectedRow?.gameId}
                </div>
                <div className="mb-3">
                  <strong>밴더 :</strong>{" "}
                  {vendorMap.get(normalize(selectedRow?.vendorKey)) ||
                    selectedRow?.vendorKey}
                </div>

                <input
                  type="text"
                  className="form-control"
                  placeholder="게임명(한글)을 입력하세요"
                  value={korName}
                  onChange={(e) => setKorName(e.target.value)}
                />
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  취소
                </button>

                <button
                  className="btn btn-primary"
                  disabled={!korName || saving}
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const token = localStorage.getItem("token");

                      await axios.post(
                        `${API_BASE}/api/GameKorUpdate`,
                        {
                          game_id: selectedRow.gameId,
                          vcode: selectedRow.vendorKey,
                          game_name_kor: korName,
                        },
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                          },
                        },
                      );

                      setShowModal(false);
                      fetchGameList(); // 🔥 KR 반영
                    } catch (e) {
                      alert("저장 실패");
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
